import {
  Button, Loading, useRecordContext, useTranslate, useCreatePath,
  Link,
} from 'react-admin';
import {
  DialogTitle, DialogContent, Dialog,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  DialogActions,
  Typography,
  Select,
  MenuItem,
  Box,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  useState, useEffect, useCallback, ReactNode,
} from 'react';
import { Asset, CertificationProperties } from '@x-guard/xgac-types/xgac';
import { xgacDataProvider } from '../../dataProviders/xgacDataProvider';
import { CertificationExpiryWithDaysCaption } from '../CertificationExpiryWithDaysCaption';
import { getExpiryStatus } from '../../utils/certificationsAndItemsExpiry';
import type { ExpiryStatus } from '../../utils/certificationsAndItemsExpiry';

type CertifiedUserInfo = {
  _id: string;
  name: string;
  firstIssuedOn: string;
  lastReceivedOn: string;
  expiryDate: string;
  type: ExpiryStatus;
};

const DIALOG_TITLE_MESSAGE = 'resources.certification-categories.text.users_dialog_title';
const EMPTY_DIALOG_MESSAGE = 'resources.certification-categories.text.users_dialog_empty';
const FILTER_OPTIONS: { label: string; value: ExpiryStatus }[] = [
  { label: 'resources.certification-categories.text.certified', value: 'valid' },
  { label: 'resources.certification-categories.text.expiring', value: 'expiring' },
  { label: 'resources.certification-categories.text.expired', value: 'expired' },
];

const UsersDialog = (props: {
  open: boolean;
  onClose: () => void;
  types: ExpiryStatus[];
  expiringCertificationsInfo: CertifiedUserInfo[];
  expiredCertificationsInfo: CertifiedUserInfo[];
  validCertificationsInfo: CertifiedUserInfo[];
  certificationCategoryName: string;
  largestReminderPeriodInDays: number;
  loading: boolean;
  setUsersDialogTypes: (types: ExpiryStatus[]) => void;
}) => {

  const {
    open, onClose, types, expiringCertificationsInfo,
    expiredCertificationsInfo, validCertificationsInfo, certificationCategoryName, largestReminderPeriodInDays,
    loading,
    setUsersDialogTypes,
  } = props;
  const translate = useTranslate();
  const createPath = useCreatePath();
  const [dialogBody, setDialogBody] = useState<ReactNode>(null);

  const getUsersForTypes = (
    selectedTypes: ExpiryStatus[],
    valid: CertifiedUserInfo[],
    expiring: CertifiedUserInfo[],
    expired: CertifiedUserInfo[],
  ): CertifiedUserInfo[] => {

    const byType: Record<ExpiryStatus, CertifiedUserInfo[]> = {
      valid,
      expiring,
      expired,
    };

    const existing = new Set<string>();

    return selectedTypes.flatMap((selectedType) => byType[selectedType].filter((user) => {

      if (existing.has(user._id)) {

        return false;

      }
      existing.add(user._id);
      return true;

    }));

  };

  const returnDialogBody = useCallback((certificationsInfo: CertifiedUserInfo[]) => {

    if (loading) {

      return <Loading loadingPrimary={translate('general.text.loading')} loadingSecondary={translate('general.text.loading_secondary')}/>;

    }

    if (certificationsInfo.length === 0) {

      return <Typography sx={{ textAlign: 'center', marginTop: '20px' }}>
        {translate(EMPTY_DIALOG_MESSAGE)}</Typography>;

    }

    return <>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>{translate('general.fields.name')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.first_issued_on')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.last_received_on')}</TableCell>
            <TableCell>{translate('resources.assets.certifications.fields.expiry_date')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {certificationsInfo.map((asset) => (
            <TableRow key={asset._id}>
              <TableCell>
                <Link
                  to={createPath({ resource: 'assets', type: 'edit', id: asset._id })}
                >{asset.name}
                </Link>
              </TableCell>
              <TableCell>{asset.firstIssuedOn ? new Date(asset.firstIssuedOn).toLocaleDateString() : ''}</TableCell>
              <TableCell>{asset.lastReceivedOn ? new Date(asset.lastReceivedOn).toLocaleDateString() : ''}</TableCell>
              <TableCell>
                <CertificationExpiryWithDaysCaption
                  record={{
                    ...asset,
                    id: asset._id,
                    largestReminderPeriodInDays,
                  }}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>;

  }, [loading, translate, largestReminderPeriodInDays, createPath]);

  useEffect(() => {

    const selectedTypes: ExpiryStatus[] = types.length > 0 ? types : ['valid', 'expiring', 'expired'];

    const mergedUsers = getUsersForTypes(selectedTypes, validCertificationsInfo, expiringCertificationsInfo, expiredCertificationsInfo);

    setDialogBody(returnDialogBody(mergedUsers));

  }, [types, validCertificationsInfo, expiringCertificationsInfo, expiredCertificationsInfo, returnDialogBody]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {`${translate(DIALOG_TITLE_MESSAGE)} "${certificationCategoryName ?? ''}"`}
      </DialogTitle>
      <DialogContent sx={{ minHeight: '300px' }}>
        <Box sx={{
          display: 'flex', flexDirection: 'row', gap: '5px', mb: 2, alignItems: 'center',
        }}>
          <Typography>{translate('resources.certification-categories.text.filter_by')}</Typography>
          <Select
            multiple
            value={types}
            onChange={(e) => {

              const value = e.target.value as ExpiryStatus[];
              setUsersDialogTypes(value.length > 0 ? value : ['valid', 'expiring', 'expired']);

            }}
            renderValue={(selected) => {

              return (selected as ExpiryStatus[])
                .map((value) => translate(FILTER_OPTIONS.find((option) => option.value === value)?.label ?? ''))
                .join(', ');

            }}
            sx={{ minWidth: '230px', height: '40px' }}
          >
            {FILTER_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox checked={types.includes(option.value)} />
                <ListItemText primary={translate(option.label)} />
              </MenuItem>
            ))}
          </Select>
        </Box>
        {dialogBody}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} label={translate('general.text.close')}/>
      </DialogActions>
    </Dialog>
  );

};

// disabled because the label is simply added to display the column name in the datagrid
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CertifiedUsersButton = (props: { label?: string }) => {

  const record = useRecordContext();
  const translate = useTranslate();
  const [usersDialogOpen, setUsersDialogOpen] = useState(false);
  const [certifiedUsers, setCertifiedUsers] = useState<CertifiedUserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [validCertifications, setValidCertifications] = useState<CertifiedUserInfo[]>([]);
  const [usersDialogTypes, setUsersDialogTypes] = useState<ExpiryStatus[]>(['valid']);
  const [expiringCertifications, setExpiringCertifications] = useState<CertifiedUserInfo[]>([]);
  const [expiredCertifications, setExpiredCertifications] = useState<CertifiedUserInfo[]>([]);

  useEffect(() => {

    setLoading(true);
    xgacDataProvider.getList('all-assets', {
      pagination: { page: 1, perPage: -1 },
      sort: { field: 'name', order: 'ASC' },
      filter: { 'certifications.certificationCategory._id': record?._id },
    }).then((response) => {

      const mappedAssets = response.data.map((asset: Asset) => {

        // Only the certifications for the current certification category are considered
        const certificationOfCategory = (asset.certifications ?? [])
          .find((certification: CertificationProperties) => certification.certificationCategory._id === record?._id);
        const firstIssuedOn = certificationOfCategory?.firstIssuedOn || '';
        const lastReceivedOn = certificationOfCategory?.lastReceivedOn || '';
        const expiryDate = certificationOfCategory?.expiryDate || '';

        const type = getExpiryStatus(expiryDate, record?.largestReminderPeriodInDays ?? 0);

        return {
          _id: asset._id,
          name: asset.name,
          firstIssuedOn,
          lastReceivedOn,
          expiryDate,
          type,
        };

      });

      setCertifiedUsers(mappedAssets);
      setLoading(false);

    });

  }, [record?._id, record?.largestReminderPeriodInDays]);

  useEffect(() => {

    setValidCertifications(certifiedUsers.filter((user) => user.type !== 'expired'));
    setExpiringCertifications(certifiedUsers.filter((user) => user.type === 'expiring'));
    setExpiredCertifications(certifiedUsers.filter((user) => user.type === 'expired'));

  }, [certifiedUsers]);

  return (<>
    <div style={{ display: 'flex', flexDirection: 'row', gap: '5px' }}>
      <Button
        variant={'contained'}
        disabled={validCertifications.length === 0}
        onClick={() => {

          setUsersDialogOpen(true);
          setUsersDialogTypes(['valid']);

        }}
        label={`${validCertifications.length} ${translate('resources.certification-categories.text.certified')}`}
        sx={{ whiteSpace: 'nowrap' }}
      />
      <Button
        variant={'contained'}
        color={'warning'}
        disabled={expiringCertifications.length === 0}
        onClick={() => {

          setUsersDialogOpen(true);
          setUsersDialogTypes(['expiring']);

        }}
        label={`${expiringCertifications.length} ${translate('resources.certification-categories.text.expiring')}`}
        sx={{ whiteSpace: 'nowrap' }}
      />
      <Button
        variant={'contained'}
        color={'error'}
        disabled={expiredCertifications.length === 0}
        onClick={() => {

          setUsersDialogOpen(true);
          setUsersDialogTypes(['expired']);

        }}
        label={`${expiredCertifications.length} ${translate('resources.certification-categories.text.expired')}`}
        sx={{ whiteSpace: 'nowrap' }}
      />
    </div>
    <UsersDialog
      open={usersDialogOpen}
      onClose={() => setUsersDialogOpen(false)}
      types={usersDialogTypes}
      expiringCertificationsInfo={expiringCertifications}
      expiredCertificationsInfo={expiredCertifications}
      validCertificationsInfo={validCertifications}
      certificationCategoryName={record?.name ?? ''}
      largestReminderPeriodInDays={record?.largestReminderPeriodInDays ?? 0}
      loading={loading}
      setUsersDialogTypes={setUsersDialogTypes}
    />
  </>);

};
